import { characterEntities } from "character-entities";
import { isHtmlContent } from "./noteContentFormat";
import { memoizeString } from "./utils/memoizeString";

/**
 * THE NOTE FINGERPRINT PARITY CONTRACT — both halves live in this file so
 * they can only be edited side by side.
 *
 * Note-derived artifacts (scores, fact checks, sentiment, density,
 * summaries) are keyed by a hash of the note's VISIBLE TEXT, so
 * formatting-only rewrites — the HTML→markdown storage conversion, a future
 * normalization pass — never invalidate them. That only holds if the two
 * projections here agree byte-for-byte:
 *
 *   - `htmlNoteTextStable` projects a legacy Lexical-HTML note, and
 *   - `stripMarkdownSyntax` projects its markdown conversion,
 *
 * to the SAME string for equivalent content, in EVERY environment (browser,
 * server, worker — no DOMParser anywhere on this path). Every rule added to
 * one branch needs its mirror in the other: link/image targets are kept
 *
 * positionally alike, code spans and URLs stay literal, entities decode
 * through one shared decoder. `src/tests/noteHashPlaintext.test.ts` pins the
 * pairs — extend it with any new rule.
 */

const REGEX_NBSP = /&nbsp;|&#160;|\u00a0/g;

/**
 * Decode character references the way a renderer does, DOM-free. One pass,
 * one resolver: numeric refs (hex/decimal, out-of-range stays literal — this
 * runs inside hashNotes on user-authored notes and must never throw) and the
 * full HTML named-entity map (`character-entities`, the same data remark
 * uses — pure JSON, identical in every environment). Single-pass matters:
 * chained replaces would re-decode text constructed by an earlier step
 * (`&#38;lt;` must surface as `&lt;`, not `<`).
 */
function decodeEntitiesStable(text: string): string {
  return text.replace(
    /&(?:#[xX]([0-9a-fA-F]+)|#(\d+)|([a-zA-Z][a-zA-Z0-9]*));/g,
    (ref: string, hex?: string, dec?: string, name?: string) => {
      if (hex != null || dec != null) {
        const code = hex != null ? parseInt(hex, 16) : Number(dec);

        // `&#0;` maps to U+FFFD per the HTML spec (browsers do the same) —
        // and a raw NUL must never reach hash strings, where \0 historically
        // separated fields.
        if (code === 0) {
          return "\uFFFD";
        }

        return code <= 0x10ffff ? String.fromCodePoint(code) : ref;
      }

      // Exact-case lookup: named refs are case-sensitive (`&Auml;` ≠ `&auml;`).
      return characterEntities[name!] ?? ref;
    },
  );
}

/**
 * DOM-free plain-text projection of a legacy HTML note, for hashing.
 * `richTextToPlainText` prefers DOMParser when available, so its output can
 * differ between browser and server for entities outside the common set —
 * an environment-dependent fingerprint would make current artifacts read as
 * stale (or vice versa) depending on where the hash ran.
 */
export function htmlNoteTextStable(value: string): string {
  const lined = value
    // Block boundaries become NEWLINES, not spaces: merging two paragraphs
    // is a visible (and prompt-visible) edit, so line structure belongs in
    // the fingerprint.
    .replace(/<\/(p|div|li|h[1-6]|blockquote|tr|td|th)>/gi, "$&\n")
    .replace(/<br\s*\/?>/gi, "\n");
  // Remaining tags vanish WITHOUT a space: block boundaries were handled
  // above, and inline markup can sit mid-word — `wo<strong>rd</strong>`
  // must project "word" (as its markdown form does), not "wo rd".
  // Strip comments whole (an internal `>` must not leave a dangling tail),
  // then only tag-shaped tokens: `<` starts a tag solely before a letter,
  // `/`, or `!` (per HTML parsing), so literal comparison text like
  // `2 < 3 and 5 > 4` renders as text and stays fingerprint content.
  return collapseProjectedWhitespace(
    decodeEntitiesStable(
      lined
        .replace(/<!--[\s\S]*?-->/g, "")
        .replace(/<(?:\/?[a-zA-Z]|![a-zA-Z])[^>]*>/g, "")
        .replace(REGEX_NBSP, " "),
    ),
  );
}

/**
 * Canonical whitespace for fingerprints: runs of spaces/tabs collapse to one
 * space, blank-line runs to ONE newline, edges trimmed. One `\n` per block
 * boundary on both branches keeps cross-format parity (HTML block closers
 * and markdown's `\n\n` both land here as a single `\n`) while structure
 * edits still change the projection.
 */
function collapseProjectedWhitespace(text: string): string {
  return text
    .replace(/[ \t\u00a0]+/g, " ")
    .replace(/ *\n[ \t\u00a0\n]*/g, "\n")
    .trim();
}

/**
 * Strip markdown syntax to plain text, preserving line structure.
 * Deterministic string ops, intentionally not a markdown parser — this is a
 * projection for previews/word-counts/blank-checks, mirroring what
 * `htmlToPlainText` does for the HTML shape. Stored notes escape literal
 * markdown characters (`\*`), so unescaped markers really are formatting;
 * `_` is only stripped at word boundaries because intraword underscores
 * (snake_case) aren't emphasis in the first place.
 */
export function stripMarkdownSyntax(
  value: string,
  options?: {
    keepLinkTargets?: boolean;
    rewriteMedia?: boolean;
    // Project raw HTML islands to their RENDERED text (anchor labels kept,
    // tag-shaped tokens and comments dropped) — for matching quotes copied
    // from the rendered note. Runs after code protection, so a tag typed
    // inside a code span stays literal.
    htmlIslandsVisible?: boolean;
    // Collapse projected whitespace BEFORE code placeholders restore, so
    // literal indentation/blank lines inside code stay fingerprint content
    // (the renderer shows them; a code-only realignment must re-key).
    collapseBeforeRestore?: boolean;
  },
): string {
  // Placeholder machinery FIRST: fence interiors must be protected before
  // the per-line marker strip below, or a literal `- flag` / `# title` line
  // inside a code block loses its prefix from the fingerprint. Placeholders
  // are single-line, so the later line split is unaffected.
  const literals: string[] = [];

  const protect = (body: string) => {
    literals.push(body);
    return `\uE000${literals.length - 1}\uE001`;
  };

  // Fences may be indented up to three spaces (CommonMark); four would be
  // an INDENTED code block, which this projection deliberately does NOT
  // protect. That's not laziness: whether a 4-space line is code depends on
  // block context (a loose nested list's depth-two item is also a 4-space
  // line after a blank), so a regex rule would misclassify real converted
  // notes' nested lists as code and break HTML↔markdown parity for them.
  // Handling it correctly means a full CommonMark block parser; Lexical and
  // the converter never emit indented code, so the residual is
  // agent-authored notes using the one code form the editor can't produce.
  const fenceProtected = value
    // Fence lines are formatting, the interior is literal text. An unclosed
    // fence runs to end of input, per CommonMark.
    // The interior is optional: an EMPTY fence (opener line immediately
    // followed by the closer) has only one newline between them, and missing
    // it would misroute to the unclosed rule — swallowing everything after
    // the fence as literal code.
    // The closer must repeat the OPENER's character with at least its
    // length (CommonMark): `\2\3*` is the opener string plus optional extra
    // fence chars, so a ~~~ line inside a backtick fence — or a too-short
    // closer under a four-tick opener — stays part of the code.
    .replace(
      /(^|\n)[ ]{0,3}(([`~])\3{2,})[^\n]*\n(?:([\s\S]*?)\n)?[ ]{0,3}\2\3*[ \t]*(?=\n|$)/g,
      (
        _m,
        lead: string,
        _open: string,
        _ch: string,
        body: string | undefined,
      ) => lead + protect(body ?? ""),
    )
    .replace(
      /(^|\n)[ ]{0,3}(?:`{3,}|~{3,})[^\n]*\n([\s\S]*)$/g,
      (_m, lead: string, body: string) => lead + protect(body),
    );

  const lines = fenceProtected.split("\n").map((line) => {
    // List markers: strip repeatedly — `- - x` (an item whose content is a
    // nested list, e.g. a note that STARTS with an indented bullet) stacks
    // markers on one line, and a marker-only line is an empty list item.
    // Either way the markers are bullets, not text.
    let stripped = line;

    for (;;) {
      const next = stripped.replace(/^(\s*)(?:[-*+]|\d+[.)])(?:\s+|$)/, "$1");

      if (next === stripped) {
        break;
      }

      stripped = next;
    }

    return stripped
      .replace(/^(\s*)#{1,6}\s+/, "$1") // headings
      .replace(/^(\s*)>\s?/, "$1"); // blockquotes
  });
  // Link/image targets and inline-code interiors are literal text too — a
  // `*` or `_` inside a URL or a code span renders as-is and must survive
  // the delimiter rules below.
  let joined = lines
    .join("\n")
    // Code spans: the delimiter backticks go, the interior stays verbatim.
    // The body may contain backtick runs SHORTER than the delimiter
    // (CommonMark: `` `**ACME**` `` is a double-tick span holding a literal
    // single-tick span), so the body forbids only the delimiter run itself —
    // and the closer can't borrow a body-trailing backtick.
    .replace(
      /(?<!\\)(?<!`)(`+)((?:(?!\1)[^\n])+?)(?<!`)\1(?!`)/g,
      (_m, _ticks, body: string) => protect(body),
    );

  // Media rewriting happens HERE, after fence and code-span interiors are
  // placeholder-protected: a media tag typed inside code is literal text
  // (the projection's code contract), not an embed to reduce to its target.
  if (options?.rewriteMedia) {
    joined = rewriteMediaTags(joined);
  }

  if (options?.htmlIslandsVisible) {
    joined = joined
      // Angle autolinks FIRST: `<https://x>` / `<me@x.test>` render as their
      // text, and the tag strip below would otherwise eat them as raw HTML.
      // A backslash-escaped `<` is literal text the renderer SHOWS
      // (`\<script>` renders as "<script>") — every island rule below
      // skips escaped angles so that text survives into the projection.
      .replace(
        /(?<!\\)<([a-zA-Z][a-zA-Z0-9+.-]{1,31}:[^<>\s]*|[^\s<>@]+@[^\s<>]+\.[^\s<>]+)>/g,
        "$1",
      )
      .replace(/(?<!\\)<a\s[^>]*>([\s\S]*?)<\/a>/gi, "$1")
      .replace(/(?<!\\)<!--[\s\S]*?-->/g, "")
      // Break/block tags render as visible boundaries — project them as
      // separators (same as the HTML branch) before erasing the rest, or
      // `Alpha<br>Beta` would fuse into "AlphaBeta" and rendered-quote
      // matching would fail.
      .replace(/(?<!\\)<br\s*\/?>/gi, "\n")
      .replace(
        /(?<!\\)<\/?(?:p|div|li|h[1-6]|blockquote|tr|td|th)(?:\s[^>]*)?>/gi,
        "\n",
      )
      .replace(/(?<!\\)<(?:\/?[a-zA-Z]|![a-zA-Z])[^>]*>/g, "");
  }

  const linked = joined
    // Images FIRST, then links — innermost out, so a linked image
    // `[![alt](src)](href)` (the converter's shape for <a><img></a>)
    // resolves to `alt (src) (href)` instead of a mangled half-match.
    // For fingerprinting (keepLinkTargets) targets are kept — they're
    // content a reader follows and the LLMs see, so a target-only edit must
    // not read as "unchanged" — and their entity refs decode HERE
    // (protection hides them from the final decode pass), matching the HTML
    // branch's decoded hrefs. Code-span literals stay raw by contrast:
    // CommonMark does NOT decode inside code.
    .replace(
      /!\[([^\]]*)\]\(((?:[^()\n]|\([^()\n]*\))*)\)/g,
      (_m, alt: string, src: string) => {
        if (!options?.keepLinkTargets) {
          return alt;
        }

        return `${alt} (${protect(decodeEntitiesStable(src))})`;
      },
    )
    // Destinations may hold one level of balanced parens (`Foo_(bar)`, the
    // Wikipedia shape): stopping at the first `)` would leave a stray paren
    // in the projection, so rendered-text quotes stop matching.
    .replace(
      /\[([^\]]*)\]\(((?:[^()\n]|\([^()\n]*\))*)\)/g,
      (_m, label: string, url: string) => {
        if (!options?.keepLinkTargets) {
          return label;
        }

        return `${label} (${protect(decodeEntitiesStable(url))})`;
      },
    );
  // Strip emphasis/strike delimiters only where they form a plausible PAIR:
  // a flanking opener with a matching closer. Unpaired or whitespace-isolated
  // markers — `score was 5*`, `2 * 3 = 6` — render literally, so they must
  // stay in the projection (and the fingerprint) or an edit to them wouldn't
  // invalidate. (Stray backticks stay too; real code spans were protected
  // above.) Innermost pairs strip on later passes (`**a *b* c**`); repeat to
  // a fix point, capped defensively.
  // Pair matching is worst-case quadratic on adversarial marker soup (long
  // runs of unclosed openers force a full lazy scan per position), and this
  // runs inside hashNotes on request paths with no tight upstream size cap.
  // Real notes are fine — the largest corpus note (16KB) projects in <4ms —
  // so past a generous bound, fall back to the linear flanking-only rule
  // (slightly coarser: unpaired flanking markers strip too).
  const PAIRING_MAX_LENGTH = 100_000;
  let strippedText = linked;

  if (linked.length > PAIRING_MAX_LENGTH) {
    strippedText = linked
      .replace(/(?<!\\)(\*{1,3}|~~)(?=\S)/g, "")
      .replace(/(?<=\S)(?<!\\)(\*{1,3}|~~)/g, "")
      .replace(/(?<![\\\w])_{1,3}(?=\S)/g, "")
      .replace(/(?<=[^\s\\])_{1,3}(?!\w)/g, "");
  } else {
    for (let i = 0; i < 8; i++) {
      const next = strippedText
        .replace(/(?<!\\)(\*{1,3}|~~)(?=\S)([\s\S]+?)(?<=\S)\1/g, "$2")
        .replace(
          /(?<![\\\w])(_{1,3})(?=\S)([\s\S]+?)(?<=[^\s\\])\1(?!\w)/g,
          "$2",
        );

      if (next === strippedText) {
        break;
      }

      strippedText = next;
    }
  }

  const withBreaks = strippedText
    // hard-break backslash at end of line
    .replace(/\\\n/g, "\n");
  // Character references are markdown syntax (the serializer emits e.g.
  // `&#x20;` to keep a space from forming a list marker at line start, and
  // CommonMark decodes named refs like `&mdash;` too) — surface the
  // character the reader sees, via the same environment-stable decoder the
  // HTML branch uses. Then unescape markdown escapes LAST, so a literal
  // `\*` survives the marker strip above and lands as `*` — CommonMark's
  // full escapable-punctuation set, since the serializer escapes anything
  // context-ambiguous (`\@` in emails so GFM doesn't autolink).
  let text = decodeEntitiesStable(withBreaks).replace(
    /\\([!-/:-@[-`{-~])/g,
    "$1",
  );

  if (options?.collapseBeforeRestore) {
    text = collapseProjectedWhitespace(text);
  }

  const restored = literals.length
    ? text.replace(
        /\uE000(\d+)\uE001/g,
        (_m, i: string) => literals[Number(i)]!,
      )
    : text;
  // Restoring a literal can re-expose edge whitespace the collapse already
  // canonicalized around its placeholder (an EMPTY fence at the note edge
  // restores to "", leaving its separator newline dangling). Edge-trim once
  // more — edges are canonicalized everywhere; interior code whitespace is
  // untouched.
  return options?.collapseBeforeRestore ? restored.trim() : restored;
}

/**
 * Project a note field to the visible text its readers (and the LLMs) see.
 * Note hashes fingerprint THIS, not raw bytes, so formatting-only rewrites —
 * the HTML→markdown storage conversion, a future normalization pass — leave
 * note-derived artifacts alone.
 */
/**
 * Media elements project to their TARGETS, not to nothing: a media-only note
 * must not fingerprint as empty (isRichTextBlank counts it as content and
 * the prompts carry the markup), and two different embeds must differ.
 * Mirrors markdown's `![alt](src)` → "alt (src)" for images; video/iframe
 * have no alt, so just the source.
 */
// Attribute values in agent-written raw HTML may be quoted or (equally
// valid) unquoted — `href=https://x` ends at whitespace or `>` per the HTML
// spec. Target preservation must not depend on the author's quoting style.
function attrValue(tag: string, name: string): string {
  // Optional whitespace around `=` is valid HTML too (`src = "url"`).
  const m = new RegExp(
    `\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>"']+))`,
    "i",
  ).exec(tag);
  return m?.[1] ?? m?.[2] ?? m?.[3] ?? "";
}

function rewriteMediaTags(value: string): string {
  return (
    value
      .replace(/<img\s[^>]*\/?>/gi, (tag) => {
        // Attribute order in raw HTML is arbitrary — pull each out separately.
        return ` ${attrValue(tag, "alt")} (${attrValue(tag, "src")}) `;
      })
      .replace(/<(?:video|iframe)(?:\s[^>]*)?>/gi, (tag) => {
        return ` (${attrValue(tag, "src")}) `;
      })
      // Nested <source> children carry the real URLs in the common
      // `<video><source src=…></video>` shape — keep those targets too.
      .replace(/<source\s[^>]*\/?>/gi, (tag) => {
        return ` (${attrValue(tag, "src")}) `;
      })
      // Closing tags go too — the markdown branch never tag-strips, so a
      // leftover </video> would survive there and break cross-format parity.
      .replace(/<\/(?:video|iframe)>/gi, " ")
  );
}

// Hashing a round revisits the same note through quality, density,
// consistency, and informedness. Cache the projection by its EXACT text, not
// its hash: a content edit always computes a new projection, while all hash
// consumers share the unchanged one. The bound applies in every runtime.
export const noteTextForHash = memoizeString(projectNoteTextForHash);

function projectNoteTextForHash(value: string): string {
  if (isHtmlContent(value)) {
    // Keep link/image targets on this branch too (positionally, matching the
    // markdown branch's `label (url)`), so the same linked note fingerprints
    // identically as legacy HTML and as its markdown conversion — and a
    // target-only edit invalidates either way. The editor never produced
    // links, but agents can post raw HTML.
    const withTargets = rewriteMediaTags(
      value.replace(
        /<a\s([^>]*)>([\s\S]*?)<\/a>/gi,
        (whole, attrs: string, label: string) =>
          /\shref\s*=/i.test(` ${attrs}`)
            ? `${label} (${attrValue(` ${attrs}`, "href")})`
            : whole,
      ),
    );
    // DOM-free projection: the DOMParser path decodes entities differently
    // than the server fallback, and this hash must match across browser,
    // server, and worker.
    return htmlNoteTextStable(withTargets);
  }

  // Markdown branch keeps link/image TARGETS: they're real content (readers
  // follow them; LLM prompts carry the raw note), so a target-only edit must
  // change the fingerprint. Formatting markers still don't. Media tags in
  // raw-HTML islands get the same treatment, so a converted media note
  // fingerprints identically across formats.
  return stripMarkdownSyntax(value, {
    keepLinkTargets: true,
    rewriteMedia: true,
    collapseBeforeRestore: true,
  });
}
